/**
 * Gate for imperative (non-hook) Convex calls.
 *
 * Attaching the Clerk token is asynchronous, and the stores can reach for data
 * before it lands — on a cold load the persisted auth state rehydrates first,
 * so a store believes the user is signed in while the Convex client is still
 * anonymous. Every Convex function calls `requireUser`, so those early reads
 * came back as "Not authenticated" server errors.
 *
 * React components using `useQuery` don't need this: Convex re-runs their
 * subscriptions when auth changes. Imperative callers await `whenConvexReady()`.
 *
 * Lives apart from the client itself because only the client construction is
 * platform-specific — the web reads `import.meta.env`, Expo reads
 * `process.env.EXPO_PUBLIC_*`. This logic is identical on both and is shared
 * rather than copied into a `.native` sibling, where the reasoning above would
 * quietly drift out of step with the bug it describes.
 */
let ready: Promise<void>
let markReady: () => void
let isReady = false

function reset() {
  isReady = false
  ready = new Promise<void>((resolve) => {
    markReady = resolve
  })
}

reset()

/** Called by the auth bridge whenever Convex's auth state changes. */
export function setConvexAuthenticated(isAuthenticated: boolean) {
  if (isAuthenticated) {
    if (!isReady) {
      isReady = true
      markReady()
    }
    return
  }

  // Only replace the promise on a real sign-out. Convex reports
  // `isAuthenticated: false` while it is still connecting, and resetting then
  // would swap out the promise callers are already awaiting — leaving them
  // waiting on one that never resolves, so their reads simply never happen.
  if (isReady) {
    reset()
  }
}

/** Resolves once the Convex client is carrying an authenticated identity. */
export function whenConvexReady(): Promise<void> {
  return ready
}
